import { Service, streamSSE } from '@roc/feature-app-core';

// ───────────────────────────────────────────────────────────────────────────
// New Track Record AI extraction agent (added for the new CRM / internal Track
// Record UI — @roc/feature-track-record-v2). These additions are append-only:
// no existing method/signature was changed, so other consumers of this shared
// service (e.g. mobile-app) are unaffected.
//
// The agent streams its progress (incremental `trace` events, then `result`
// events). That needs a streaming HTTP response, which the axios-based
// `Service` base class (this.get/this.post) can't do. The fetch + auth + SSE
// framing is shared in `streamSSE` (@roc/feature-app-core); `invokeTrackRecordAgent`
// below only maps the agent's event types onto callbacks.
// ───────────────────────────────────────────────────────────────────────────
export interface TrackRecordAgentRequest {
  borrower_id: number;
  loan_id?: number;
  gc_license?: string;
  re_license?: string;
  track_record_excel?: string;
  track_record_entity?: string;
  track_record_property_address?: string;
  entity_names?: string[];
  custom_transactions?: Array<{
    street_address?: string;
    street_address_line_2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  }>;
  // Deal Type checkboxes (property look-up / manual add)
  bridge?: boolean;
  ground_up?: boolean;
  term?: boolean;
  // Explicit record_source_type override (e.g. "User Inputted" for manual add).
  record_source_type?: string;
  // Manual add ("property not listed -> add it now")
  manual_property?: {
    property_address?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    type_of_deal?: string;
    purchase_price?: string;
    purchase_date?: string;
    sale_price?: string;
    sale_date?: string;
    renovation_budget?: string;
  };
}

export interface TrackRecordTrace {
  step: string;
  tool?: string;
  detail?: string;
}

export interface TrackRecordAgentResponseValidation {
  std_addr?: string;
  matching_transactions?: any[];
  placekey?: string;
  isPropertyVerified?: boolean;
  isBorrowerVerified?: boolean;
  isVerified?: boolean;
  titleHistoryVerified?: boolean;
  purchaseDate?: string;
  purchasePrice?: string;
}

export interface TrackRecordAgentResponse {
  'NO. #'?: string;
  'Entity Name'?: string;
  'Property Address'?: string;
  City?: string;
  State?: string;
  Zipcode?: string;
  'Type of Deal'?: string;
  'Purchase Price'?: string;
  'Purchase Date'?: string;
  'Renovation Budget'?: string;
  'Sale Price'?: string;
  'Sale Date'?: string;
  validation?: TrackRecordAgentResponseValidation;
}

export interface TrackRecordAgentCallbacks {
  onTrace: (trace: TrackRecordTrace) => void;
  onResult: (results: TrackRecordAgentResponse[]) => void;
  onResultRaw: (raw: string) => void;
  onError: (error: Error) => void;
  onComplete: () => void;
}


/**
 * Payload for POST /api/v1/doc-review/docReviewQueueProcessor/enqueue.
 * Only the fields the license flow needs are declared
 */
export interface DocReviewEnqueueRequest {
  borrowerId: number;
  taskCode: string;
  filePath: string;
  taskId?: number;
}

const url = {
  ALL_VERIFICATION_REQUESTS:
    '/api/v1/loan/borrowerDetailsVerification/getAllBorrowerDetailsVerificationRequests',
  SUBMIT_VERIFICATION_REQUEST:
    '/api/v1/loan/borrowerDetailsVerification/submitBorrowerDetailsVerificationRequest',

  GET_PUBLIC_FORM_INFORMATION:
    '/api/v1/loan/public/borrowerDetailsVerification/getBorrowerDetailsPublicFormInfo',

  GET_FORM_INFORMATION:
    '/api/v1/loan/borrowerDetailsVerification/getBorrowerDetailsFormInfo',

  SAVE_BORROWER_DETAILS:
    '/api/v1/loan/public/borrowerDetailsVerification/saveBorrowerDetailsForm',

  CREATE_DETAILS_VERIFICATION_DOCUSIGN:
    '/api/v1/loan/public/borrowerDetailsVerification/createBorrowerDetailsVerificationDocusign',

  ////
  GET_DETAILS_VERIFICATION_INFO:
    '/api/v1/loan/public/borrower/borrowerDetailsVerification/getAuthInfo',

  CONFIRM_DOCUSIGN:
    '/api/v1/loan/public/borrowerDetailsVerification/confirmDocusign',

  UNSUBSCRIBED_FROM_DAILY_REMINDERS:
    '/api/v1/loan/public/borrowerDetailsVerification/unsubscribedFromReminders',

  DOWNLOAD_DRIVERS_LICENSE:
    '/api/v1/loan/public/borrowerDetailsVerification/downloadDriversLicense',
  GET_BORROWER_INFO_BY_TOKEN:
    '/api/v1/loan/public/borrowerDetailsVerification/getBorrowerInfoByToken',
  GET_VERIFIED_TRACK_RECORD_PROPERTIES: '/api/v1/loan/borrowerDetailsVerification/getVerifiedPropertiesInTrackRecord',
  GET_UNVERIFIED_TRACK_RECORD_PROPERTIES: '/api/v1/loan/borrowerDetailsVerification/getUnverifiedPropertiesInTrackRecord',
  SAVE_TRACK_RECORD_PROPERTY: '/api/v1/loan/borrowerDetailsVerification/addTrackRecord',
  EDIT_TRACK_RECORD_PROPERTY: '/api/v1/loan/borrowerDetailsVerification/editTrackRecord',
  DELETE_TRACK_RECORD_PROPERTY: '/api/v1/loan/borrowerDetailsVerification/deleteTrackRecord',
  SAVE_TRACK_RECORD_ENTITY: '/api/v1/loan/borrowerDetailsVerification/saveBorrowerEntityBorrower',
  EDIT_TRACK_RECORD_ENTITY: '/api/v1/loan/borrowerDetailsVerification/editBorrowerEntity',
  REMOVE_TRACK_RECORD_ENTITY: '/api/v1/loan/borrowerDetailsVerification/removeBorrowerEntityBorrower',
  GET_TASKS: '/api/v1/loan/borrower/public/getTasks',
  GET_BORROWER_TASKS: '/api/v1/tasks/public/getBorrowerTasksByName',
  CREATE_TASK: '/api/v1/loan/borrower/public/createTasks',
  UPLOAD_TASK_FILE: '/api/v1/loan/borrower/public/uploadTaskFile',
  DOWNLOAD_TASK_FILE: '/api/v1/loan/borrower/downloadTaskFile',
  GET_FILE_PATH: '/api/v1/document/public/getFilePath',
  SAVE_BORROWER_GC_BROKER_DATA: '/api/v1/loan/borrowerDetailsVerification/saveBorrowerGCAndBrokerData',
  GET_BORROWER_TIER: '/api/v1/loan/public/borrowerDetailsVerification/getBorrowerUnverifiedTierRating',
  IS_FEATURE_ENABLED_FOR_BORROWER_DETAILS_VERIFICATION: '/api/v1/user/public/isFeatureEnabledForBorrowerDetailsVerification',
  GET_FORM_INFORMATION_BY_BORROWER_ID: '/api/v1/loan/borrowerDetailsVerification/getBorrowerDetailsFormInfoByBorrowerId',
  GET_FORM_INFORMATION_BY_BORROWER_ID_FOR_LOAN_APPLICATIONS: '/api/v1/loan/borrowerDetailsVerification/getLoanApplicationBorrowerDetailsFormInfoByBorrowerId',
  PARSE_TRACK_RECORD_TEMPLATE: '/api/v1/loan/borrowerDetailsVerification/parseTrackRecordTemplate',
  IS_TRACK_RECORD_TEMPLATE_PARSING_ALLOWED: '/api/v1/user/public/isTrackRecordTemplateParsingAllowed',
  GET_LOAN_BORROWER_TASK: '/api/v1/loan/getBorrowerLoanTaskByName',
  INVOKE_TRACK_RECORD_AGENT: '/api/v1/ai/bedrock-agent/invoke',
  GET_BEDROCK_AGENT_INVOCATION: '/api/v1/ai/bedrock-agent/invocation',
  FIND_BY_TASK_ID: '/api/v1/taskV2/findByTaskId',
  FIND_BY_TYPE1_AND_TYPE2: '/api/v1/taskV2/findByType1AndType2',
  FIND_BY_TYPE1: '/api/v1/taskV2/findByType1',
  ENQUEUE_DOC_REVIEW: '/api/v1/doc-review/docReviewQueueProcessor/enqueue',
  GET_TRACK_RECORD_OVERVIEW: '/api/v1/ai/track-record/overview',
};

export class BorrowerDetailsVerificationService extends Service {
  getAllBorrowerDetailsVerificationRequests(
    page: number,
    pageSize: number,
    sortDir: string,
    sortBy: string,
    filters: object,
    dropdownFilters: object
  ) {
    return this.get(
      url.ALL_VERIFICATION_REQUESTS,
      {
        page,
        pageSize,
        sortDir,
        sortBy,
        filters,
        dropdownFilters,
      },
      {
        disableGlobalLoading: true,
      }
    );
  }

  submitBorrowerDetailsVerificationRequest(request) {
    return this.post(url.SUBMIT_VERIFICATION_REQUEST, request);
  }

  getFormInformation(token) {
    return this.get(url.GET_FORM_INFORMATION, { token });
  }

  getPublicFormInformation(token) {
    return this.get(url.GET_PUBLIC_FORM_INFORMATION, { token });
  }

  saveBorrowerDetails(borrowerDetails, driversLicense: File) {
    const borrowerDetailsJson = new Blob([JSON.stringify(borrowerDetails)], {
      type: 'application/json',
    });

    const formData = new FormData();
    formData.append('borrowerDetailsForm', borrowerDetailsJson);
    formData.append('driversLicense', driversLicense);

    return this.post(url.SAVE_BORROWER_DETAILS, formData);
  }

  createBorrowerDetailsVerificationDocusign(token) {
    return this.post(`${url.CREATE_DETAILS_VERIFICATION_DOCUSIGN}`, { token });
  }

  ///////

  getAuthInfo(requestId) {
    return this.get(
      `${url.GET_DETAILS_VERIFICATION_INFO}?requestId=${requestId}`
    );
  }

  confirmDocusign(token) {
    return this.post(`${url.CONFIRM_DOCUSIGN}?token=${token}`, null);
  }

  unsubscribedFromDailyReminders(token) {
    return this.post(
      `${url.UNSUBSCRIBED_FROM_DAILY_REMINDERS}?token=${token}`,
      null
    );
  }

  downloadDriversLicense(requestId) {
    return this.get(
      url.DOWNLOAD_DRIVERS_LICENSE,
      {
        requestId,
      },
      {
        responseType: 'blob',
      }
    );
  }

  getBorrowerInfoByToken(token) {
    return this.get(url.GET_BORROWER_INFO_BY_TOKEN, { token });
  }

  getVerifiedTrackRecordProperties(borrowerId: number) {
    return this.get(url.GET_VERIFIED_TRACK_RECORD_PROPERTIES, { borrowerId });
  }

  getUnverifiedTrackRecordProperties(borrowerId: number) {
    return this.get(url.GET_UNVERIFIED_TRACK_RECORD_PROPERTIES, { borrowerId });
  }

  saveTrackRecordProperty(data: any) {
    return this.post(url.SAVE_TRACK_RECORD_PROPERTY, data);
  }

  editTrackRecordProperty(data: any) {
    return this.post(url.EDIT_TRACK_RECORD_PROPERTY, data);
  }

  deleteTrackRecordProperty(id: number) {
    return this.delete(`${url.DELETE_TRACK_RECORD_PROPERTY}/${id}`);
  }

  saveTrackRecordEntity(data: any, id: number) {
    return this.post(`${url.SAVE_TRACK_RECORD_ENTITY}?borrowerId=${id}`, data);
  }

  editTrackRecordEntity(data: any, id: number) {
    return this.post(
      `${url.EDIT_TRACK_RECORD_ENTITY}?borrowerEntityId=${id}`,
      data
    );
  }

  removeTrackRecordEntity(id: number) {
    return this.delete(
      `${url.REMOVE_TRACK_RECORD_ENTITY}?borrowerEntityBorrowerId=${id}`
    );
  }

  getTasks(body) {
    return this.post(url.GET_TASKS, body);
  }

  findByTaskId(taskId: number) {
    return this.get(url.FIND_BY_TASK_ID, { taskId });
  }

  /**
   * Enqueue an uploaded license document for AI doc-review automation.
   */
  enqueueDocReview(request: DocReviewEnqueueRequest) {
    return this.post(url.ENQUEUE_DOC_REVIEW, request, {}, { disableGlobalLoading: true });
  }

  findByType1AndType2(type1: string, typeId1: number, type2: string, typeId2: number) {
    return this.get(url.FIND_BY_TYPE1_AND_TYPE2, { type1, typeId1, type2, typeId2 });
  }

  findByType1(type1: string, typeId1: number) {
    return this.get(url.FIND_BY_TYPE1, { type1, typeId1 });
  }

  getBorrowerTasks(body, taskName) {
    return this.post(`${url.GET_BORROWER_TASKS}?taskName=${taskName}`, body);
  }

  createTask(body: any) {
    return this.post(url.CREATE_TASK, body);
  }

  uploadTaskFile(body: any) {
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };
    return this.post(url.UPLOAD_TASK_FILE, body, config);
  }

  getFilePath(fileDownloadId: string) {
    return this.get(url.GET_FILE_PATH, { id: fileDownloadId });
  }

  downloadTaskFile(taskId) {
    return this.get(url.DOWNLOAD_TASK_FILE + '?taskId=' + taskId, null, {
      responseType: 'arraybuffer',
    }).then(response => new Blob([response.data]));
  }

  saveBorrowerGCAndBrokerData(data: any, token: string) {
    return this.post(
      // No token in the internal flow: empty keeps the required param present without hitting the token lookup.
      `${url.SAVE_BORROWER_GC_BROKER_DATA}?token=${token ?? ''}`,
      data
    );
  }

  getBorrowerUnverifiedTierRating(token, borrowerId) {
    return this.get(url.GET_BORROWER_TIER, { token, borrowerId });
  }

  isFeatureEnabledForBorrowerDetailsVerification(token, featureName) {
    return this.get(url.IS_FEATURE_ENABLED_FOR_BORROWER_DETAILS_VERIFICATION, { token, featureName });
  }

  getFormInformationByBorrowerId(borrowerId, isPersonalGuarantor) {
    return this.get(url.GET_FORM_INFORMATION_BY_BORROWER_ID, { borrowerId, isPersonalGuarantor });
  }

  getLoanApplicationBorrowerDetailsFormInfoByBorrowerId(borrowerId, isPersonalGuarantor) {
    return this.get(url.GET_FORM_INFORMATION_BY_BORROWER_ID_FOR_LOAN_APPLICATIONS, { borrowerId, isPersonalGuarantor });
  }

  parseTrackRecordTemplate(body: any, borrowerId: number) {
    const parseTrackRecordTemplateUrl = `${url.PARSE_TRACK_RECORD_TEMPLATE}?borrowerId=${borrowerId}`
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };
    return this.post(parseTrackRecordTemplateUrl, body, config);
  }

  isTrackRecordTemplateParsingAllowed() {
    return this.get(url.IS_TRACK_RECORD_TEMPLATE_PARSING_ALLOWED);
  }

  /**
   * Invoke the Track Record AI extraction agent and stream its output.
   *
   * The endpoint returns a Server-Sent-Events stream, which the axios-based
   * `Service` base class can't consume — so we delegate the fetch + auth +
   * stream framing to the shared `streamSSE` helper (@roc/feature-app-core) and
   * only map the agent's event types onto the caller's callbacks here:
   *   - `trace`      → onTrace  (progress steps while the agent runs)
   *   - `result`     → onResult (extracted track-record rows, JSON)
   *   - `result_raw` → onResultRaw (raw, when the payload isn't valid JSON)
   * `abortController` lets the caller cancel an in-flight extraction.
   */
  // Poll-based agent: start the job (returns { bedrockAgentInvocationId, status })
  // and poll its status. Replaces the SSE flow — the agent runs server-side
  // regardless of the client. disableGlobalLoading: the agent start + status
  // polling must not block the UI with the global loading mask (the extraction
  // dialog shows its own progress).
  startTrackRecordAgent(request: TrackRecordAgentRequest) {
    return this.post(url.INVOKE_TRACK_RECORD_AGENT, request, {}, { disableGlobalLoading: true });
  }

  getTrackRecordAgentJob(bedrockAgentInvocationId: number | string) {
    return this.get(`${url.INVOKE_TRACK_RECORD_AGENT}/${bedrockAgentInvocationId}`, {}, { disableGlobalLoading: true });
  }

  getBedrockAgentInvocation(bedrockAgentInvocationId: number | string) {
    return this.get(`${url.GET_BEDROCK_AGENT_INVOCATION}/${bedrockAgentInvocationId}`, {}, { disableGlobalLoading: true });
  }

  getTrackRecordOverview(borrowerId: number | string) {
    return this.get(url.GET_TRACK_RECORD_OVERVIEW, { borrowerId }, { disableGlobalLoading: true });
  }

  async invokeTrackRecordAgent(
    request: TrackRecordAgentRequest,
    callbacks: TrackRecordAgentCallbacks,
    abortController: AbortController,
    jwtToken?: string
  ): Promise<void> {
    await streamSSE({
      url: `${this.getHost()}${url.INVOKE_TRACK_RECORD_AGENT}`,
      body: request,
      signal: abortController.signal,
      jwtToken,
      onEvent: ({ event, data }) => {
        const payload = data.trim();
        if (!payload) return;
        try {
          if (event === 'trace') {
            callbacks.onTrace(JSON.parse(payload));
          } else if (event === 'result') {
            callbacks.onResult(JSON.parse(payload));
          } else if (event === 'result_raw') {
            callbacks.onResultRaw(payload);
          }
          // other event types are ignored (matches previous behavior)
        } catch (e) {
          console.error('Error parsing SSE event:', event, e);
        }
      },
      onComplete: () => callbacks.onComplete(),
      onError: (error) => {
        console.error('TrackRecordAgent SSE error:', error);
        callbacks.onError(error);
      },
    });
  }
}
